'use client'

import { formatEventDateRange } from '@/lib/timed-ui/formatting'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import ShareButton from '../common/ShareButton'
import type { TimedUIEvent } from '@/lib/timed-ui/adapters'
import { parseVideoUrl } from '@/lib/video-embed'
import { useTheme } from '@/components/public/branding/ThemeProvider'


const PLACEHOLDER_ASPECT = 'aspect-[21/9] lg:aspect-[3.5/1]'

// Maps admin banner display mode to aspect ratio class
const BANNER_ASPECT: Record<string, string> = {
  standard: 'aspect-[3/1]',
  tall: 'aspect-video',
  compact: 'aspect-[4/1]',
  // 'full' uses natural image proportions (no fixed aspect)
}

interface EventHeroProps {
  event: TimedUIEvent
}

const EventHero = ({ event }: EventHeroProps) => {
  const formatPrice = useFormatPrice()
  const { branding } = useTheme()
  // branding.publicSite.features.enableSocialSharing — absent/true shows the
  // share button (current behavior); explicit false hides it.
  const enableSocialSharing =
    (branding?.publicSite as { features?: { enableSocialSharing?: boolean } } | undefined)
      ?.features?.enableSocialSharing !== false
  if (!event) return null

  const eventImage = event.bannerUrl || event.largePic || event.imageUrl || ''
  // Exclude addon ticket types so the "From $X" label reflects the lowest
  // actual ticket price, not a t-shirt or parking pass that happens to be
  // cheaper than the entry ticket.
  const pricedTickets = event.ticketTypes.filter(t => t.allInPrice > 0 && !t.isAddon)
  const minPrice = pricedTickets.length > 0
    ? Math.min(...pricedTickets.map(t => t.allInPrice))
    : 0

  const isVideoBanner = event.bannerDisplayMode === 'video' && event.videoUrl
  const videoInfo = isVideoBanner ? parseVideoUrl(event.videoUrl!, event.videoEmbedOptions) : null
  const mode = event.bannerDisplayMode || 'standard'
  const aspectClass = BANNER_ASPECT[mode] || ''

  return (
    <div className="w-full max-w-full bg-[rgb(var(--color-hero-bg))]">
      <div className="px-5 lg:px-10 pt-5 lg:pt-6">
        <div className="max-w-page mx-auto">
          {isVideoBanner && videoInfo ? (
            <div className="relative rounded-2xl overflow-hidden aspect-video">
              <iframe
                src={videoInfo.embedUrl}
                title={event.title}
                className="absolute inset-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="eager"
              />
            </div>
          ) : eventImage ? (
            aspectClass ? (
              // Fixed aspect ratio modes: standard (3:1), tall (16:9), compact (4:1)
              <div className={`relative ${aspectClass} rounded-2xl overflow-hidden`}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={eventImage}
                  alt={event.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="eager"
                />
              </div>
            ) : (
              // 'full' mode: natural image proportions, no cropping
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={eventImage}
                alt={event.title}
                className="rounded-2xl"
                style={{ width: '100%', height: 'auto', display: 'block' }}
                loading="eager"
              />
            )
          ) : (
            <div className={`${PLACEHOLDER_ASPECT} relative bg-surface-tertiary rounded-2xl overflow-hidden`}>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-32 h-32 lg:w-48 lg:h-48 text-text-tertiary opacity-20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={0.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                </svg>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="px-5 lg:px-10 py-5 lg:py-6">
        <div className="max-w-page mx-auto">
          <h1 className="text-2xl md:text-3xl lg:text-hero font-heading font-extrabold text-text-primary mb-3 leading-tight line-clamp-2">
            {event.title}
          </h1>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-text-secondary text-sm">
            {event.startDate && (
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="font-medium">{formatEventDateRange(event.startDate, event.endDate, true, event.timezone)}</span>
              </div>
            )}

            {event.venue && (
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{event.venue.name}</span>
              </div>
            )}

            {/* A sold-out page has nothing to buy "from". */}
            {minPrice > 0 && event.status !== 'SOLD_OUT' && (
              <div className="flex items-center gap-1.5">
                <svg className="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
                <span>From {formatPrice(minPrice)}</span>
              </div>
            )}

            {enableSocialSharing && (
              <ShareButton
                url={typeof window !== 'undefined' ? window.location.href : ''}
                title={event.title}
                text={`Check out ${event.title}!`}
                variant="icon"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default EventHero
